import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import MenuButton from '@/components/common/MenuButton';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import FilterOperator from '@/constants/forms/FilterOperator';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import useStatefulId from '@/lib/hooks/useStatefulId';
import translate from '@/lib/intl/translate';
import AdvancedFilterCriteria from '../constants/AdvancedFilterCriteria';
import DefaultValueMap from '../constants/DefaultValueMap';
import FieldLabelMap from '../constants/FieldLabelMap';
import FieldOperatorsMap from '../constants/FieldOperatorsMap';
import isDonationField from '../utils/isDonationField';
import sortFilterFields from '../utils/sortFilterFields';
import AdvancedFilterOperatorSelect from './AdvancedFilterOperatorSelect';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import AdvancedFilterValueInput from './AdvancedFilterValueInput';

const { IS_NULL, IS_NOT_NULL } = FilterOperator;

export type AdvancedFilterFieldsetProps = {
  onDuplicate: () => void;
  onRemove: () => void;
  fieldArrayName?: string;
  index?: number;
};

export default function AdvancedFilterFieldset({
  onDuplicate,
  onRemove,
  fieldArrayName,
  index,
}: AdvancedFilterFieldsetProps) {
  const [hasInitiallyRendered, setHasInitiallyRendered] = React.useReducer(
    () => true,
    false,
  );

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const openMenu = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) =>
      setMenuAnchor(event.currentTarget),
    [],
  );
  const closeMenu = React.useCallback(() => setMenuAnchor(null), []);
  const uid = useStatefulId();

  const isInFieldArray = fieldArrayName && typeof index !== 'undefined';
  const fieldArrayPrefix = isInFieldArray ? `${fieldArrayName}.${index}.` : '';

  const { control, getValues, setValue, watch } = useFormContext();
  const watchedFieldValue = watch(`${fieldArrayPrefix}field`);
  const watchedOperatorValue = watch(`${fieldArrayPrefix}operator`);

  const { data: customFields } = useCustomFields();
  const selectedCustomField = React.useMemo(
    () =>
      (customFields || []).find(
        ({ customFieldId }) => customFieldId === watchedFieldValue,
      ),
    [customFields, watchedFieldValue],
  );

  const handleDuplicate = React.useCallback(() => {
    onDuplicate();
    closeMenu();
  }, [onDuplicate]);

  const handleRemove = React.useCallback(() => {
    onRemove();
    closeMenu();
  }, [onRemove]);

  React.useEffect(() => {
    if (watchedFieldValue) {
      const fieldKey = selectedCustomField
        ? selectedCustomField.fieldType
        : (watchedFieldValue as ConstituentSearchFilterField);
      const operatorKey = `${fieldArrayPrefix}operator`;
      const valueKey = `${fieldArrayPrefix}value`;
      const [operatorValue, value] = getValues([operatorKey, valueKey]);

      if (
        operatorValue === '' ||
        !FieldOperatorsMap[fieldKey].includes(operatorValue)
      ) {
        setValue(operatorKey, FieldOperatorsMap[fieldKey][0]);
      }
      if (value === undefined || hasInitiallyRendered) {
        setValue(valueKey, DefaultValueMap[fieldKey]);
      }
    }
  }, [watchedFieldValue, fieldArrayPrefix, selectedCustomField]);

  const getLabel = React.useCallback(
    (val: string | ConstituentSearchFilterField) => {
      const standardFields: string[] = Object.values(
        ConstituentSearchFilterField,
      );

      if (standardFields.includes(val)) {
        return translate(FieldLabelMap[val as ConstituentSearchFilterField]);
      }

      return (
        (customFields || []).find(({ customFieldId }) => customFieldId === val)
          ?.fieldName || ''
      );
    },
    [customFields],
  );

  const fieldsetLegend =
    typeof index !== 'undefined'
      ? `${translate('FILTER')} ${index + 1}`
      : translate('FILTER');

  const shouldShowValueField =
    watchedOperatorValue !== IS_NULL && watchedOperatorValue !== IS_NOT_NULL;

  const options = React.useMemo(() => {
    const customFieldIds = (customFields || []).map(
      (field) => field.customFieldId,
    );
    return sortFilterFields(['', ...AdvancedFilterCriteria, ...customFieldIds]);
  }, [customFields]);

  React.useEffect(() => {
    setHasInitiallyRendered();
  }, []);

  return (
    <fieldset className="flex rounded-lg bg-grey-100 p-3">
      <legend className="sr-only">{fieldsetLegend}</legend>
      <div className="max-w-md grow">
        <FormControl className="flex flex-col">
          <Controller
            name={`${fieldArrayPrefix}field`}
            control={control}
            render={({ field: { onBlur, onChange, ...rest } }) => (
              <Autocomplete
                {...rest}
                options={options}
                onChange={(_, data) => {
                  onChange(data);
                }}
                groupBy={(option: string) =>
                  isDonationField(option)
                    ? translate('CONTRIBUTION_INFORMATION')
                    : translate('CONSTITUENT_INFORMATION')
                }
                renderOption={(props, option) => {
                  if (option === '') {
                    return null;
                  }

                  return <li {...props}>{getLabel(option)}</li>;
                }}
                getOptionLabel={(option: string) =>
                  option === '' ? '' : getLabel(option)
                }
                renderInput={(params) => (
                  <TextField
                    label={translate('FILTERS_CRITERIA_LABEL')}
                    required
                    onBlur={onBlur}
                    {...params}
                  />
                )}
              />
            )}
          />
        </FormControl>
        {watchedFieldValue && (
          <>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <AdvancedFilterOperatorSelect
                uid={uid}
                field={
                  selectedCustomField
                    ? selectedCustomField.fieldType
                    : watchedFieldValue
                }
                fieldArrayPrefix={fieldArrayPrefix}
              />
            </div>
            {shouldShowValueField && (
              <div className="mt-3 flex gap-3">
                <AdvancedFilterValueInput
                  customField={selectedCustomField}
                  operator={watchedOperatorValue}
                  uid={uid}
                  field={
                    selectedCustomField
                      ? selectedCustomField.fieldType
                      : watchedFieldValue
                  }
                  fieldArrayPrefix={fieldArrayPrefix}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="grow-0 pl-2">
        <MenuButton
          onClick={openMenu}
          className="text-text-secondary"
          aria-controls={
            menuAnchor ? `advanced-filters-fieldset-menu-${uid}` : undefined
          }
          aria-haspopup="true"
        />
        <Menu
          id={`advanced-filters-fieldset-menu-${uid}`}
          onClose={closeMenu}
          open={!!menuAnchor}
          anchorEl={menuAnchor}
        >
          <MenuItem onClick={handleDuplicate}>
            {translate('DUPLICATE_FILTER')}
          </MenuItem>
          <MenuItem onClick={handleRemove}>
            {translate('REMOVE_FILTER')}
          </MenuItem>
        </Menu>
      </div>
    </fieldset>
  );
}
